import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * Single or multi file uploader.
 * value: string (single) or string[] (multi)
 * onChange: new value
 */
const FileUploader = ({ value, onChange, multi = false, accept = "image/*", testId = "file-uploader", label }) => {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const upload = async (files) => {
    if (!files?.length) return;
    setLoading(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(res.data.url);
      }
      if (multi) {
        onChange([...(value || []), ...uploaded]);
      } else {
        onChange(uploaded[0]);
      }
      toast.success("Uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => {
    if (multi) {
      onChange(value.filter((_, i) => i !== idx));
    } else {
      onChange("");
    }
  };

  const items = multi ? (value || []) : (value ? [value] : []);

  return (
    <div>
      {label && <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">{label}</label>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        {items.map((url, idx) => (
          <div key={idx} className="relative group aspect-square bg-slate-100 rounded-sm overflow-hidden border border-slate-200">
            {accept.includes("image") ? (
              <img src={fileUrl(url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="flex items-center justify-center h-full text-xs text-slate-500 p-2 text-center break-all">{url.split("/").pop()}</div>
            )}
            <button type="button" onClick={() => remove(idx)} data-testid={`${testId}-remove-${idx}`} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => inputRef.current?.click()} disabled={loading} data-testid={`${testId}-btn`} className="border border-dashed border-slate-300 hover:border-[#D4AF37] hover:text-[#D4AF37] text-slate-500 text-xs uppercase tracking-[0.2em] px-5 py-3 rounded-sm flex items-center gap-2 disabled:opacity-50">
        {loading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        {loading ? "Uploading..." : multi ? "Add Files" : "Upload File"}
      </button>
      <input ref={inputRef} type="file" accept={accept} multiple={multi} onChange={(e) => upload(e.target.files)} className="hidden" data-testid={`${testId}-input`} />
    </div>
  );
};

export default FileUploader;
